import {
	Maybe,
	Regulation,
	RegulatoryDocument,
	Requirement,
	VexCluster,
} from 'types';
import {TFunction} from 'react-i18next';
import {
	IHTMLXL,
	IArrayRoles,
	renderHeaderXL,
	renderHTMLExcel,
	createZIPXL,
	addHTMLImagesAsLinks,
	cleanHTMLExcel,
	renderHTMLStringXL,
	renderArrayXL,
	renderDateXL,
	renderStringArrayXL,
	renderRowItemAsLinkXL,
} from './ExcelExportCommon';
import Excel from 'exceljs';

interface IRequirementPage {
	(
		entityListItems: any[],
		columnNames: string[],
		arrRoles: IArrayRoles,
		myVexClusters: VexCluster[],
		t: TFunction,
	): Promise<void>;
}

export const handleClickRequirements: IRequirementPage = async (
	entityListItems,
	columnNames,
	arrRoles,
	myVexClusters,
	t,
) => {
	let filtRequirement = entityListItems as Requirement[];

	if (arrRoles.isVex) {
		filtRequirement = filtRequirement.filter(x =>
			x.vexClusters.some(vc => myVexClusters.some(mvc => mvc.id === vc.id)),
		);
	}

	const isFAQCol = columnNames.includes('Q&As');

	const arrImagesUri: string[] = [];
	const arrImagesName: string[] = [];
	const arrTables: string[] = [];

	const workbook = new Excel.Workbook();
	const sheet = workbook.addWorksheet(
		'TRACE_' +
			t('RequirementsHeader', {
				ns: 'features/requirements',
			}),
	);
	const columns = [
		{name: ' ', filterButton: false},
		...columnNames.slice(1).map(x => {
			return {name: x, filterButton: true};
		}),
	];
	const arrColWidth: number[] = [
		10,
		60,
		...new Array(2).fill(25),
		...new Array(2).fill(40),
		...new Array(5).fill(25),
		80,
		...new Array(8).fill(25),
	];
	const strPageName = '';

	renderHeaderXL(workbook, sheet, arrColWidth, strPageName, t);

	let intRowCount = 4;
	let row = sheet.getRow(intRowCount);
	row.font = {
		bold: true,
		name: 'Porsche Next TT',
		size: 10,
		color: {argb: 'FFFFFFFF'},
	};

	sheet.addTable({
		name: 'tableRequirements',
		ref: 'B' + String(intRowCount),
		style: {
			showRowStripes: true,
		},
		columns,
		rows: [],
	});

	const table = sheet.getTable('tableRequirements');

	intRowCount++;
	const tempDivElement = document.createElement('div');
	let arrHTMLXL: IHTMLXL[];
	let arrHTMLXLClean: IHTMLXL[];

	if (filtRequirement.length === 0) {
		table.addRow(new Array(columnNames.length).fill(''));
		intRowCount++;
	}

	for (let ii = 0; ii < filtRequirement.length; ii++) {
		arrHTMLXL = [];
		arrHTMLXLClean = [];

		const iRequirement = filtRequirement[ii];
		tempDivElement.innerHTML = iRequirement.definition
			? '<div>' + iRequirement.definition + '</div>'
			: '<div></div>';
		renderHTMLExcel(tempDivElement.childNodes[0].childNodes, arrHTMLXL);
		cleanHTMLExcel(arrHTMLXL, arrHTMLXLClean);

		row = sheet.getRow(intRowCount);

		row.alignment = {wrapText: true, vertical: 'top'};

		row.getCell('D').style.font = {
			underline: true,
			bold: false,
			color: {argb: '00B0F0'},
		};

		row.getCell('U').style.font = {
			underline: true,
			bold: false,
			color: {argb: '00B0F0'},
		};

		table.addRow([
			'',
			iRequirement.name,
			{
				text: iRequirement.requirementId,
				hyperlink: window.location.origin + '/requirements/' + iRequirement.id,
			},
			iRequirement.version ?? 'V1.0',
			renderStringArrayXL(renderRegulationNumberXL(iRequirement.regulations)),
			renderStringArrayXL(
				renderRegulationVersionXL(iRequirement.regulatoryDocuments),
			),
			renderArrayXL(iRequirement.markets),
			renderArrayXL(iRequirement.vexClusters),
			renderDateXL(iRequirement.dateEffective),
			renderDateXL(iRequirement.dateEffective),
			t(iRequirement.status ?? '', {
				ns: 'common/enums',
				keyPrefix: 'RequirementStatus',
			}),
			renderHTMLStringXL('', iRequirement.definition),
			renderArrayXL(iRequirement.systemLevels),
			renderArrayXL(iRequirement.keywords),
			renderArrayXL(iRequirement.tags),
			iRequirement.createdBy.name,
			renderDateXL(iRequirement.modifiedAt),
			t(iRequirement.category, {
				ns: 'common/enums',
				keyPrefix: 'RequirementCategory',
			}),
			'',
			isFAQCol && ((iRequirement as any).question?.count ?? 0) > 0
				? {
						text: `${(iRequirement as any).question?.count}`,
						hyperlink:
							window.location.origin + '/requirements/' + iRequirement.id,
				  }
				: '',
		]);

		intRowCount++;

		intRowCount = addHTMLImagesAsLinks(
			intRowCount,
			iRequirement.requirementId as string,
			arrImagesName,
			arrImagesUri,
			'J',
			8,
			tempDivElement,
			sheet,
			table,
			t,
		);

		const arrAttach = iRequirement.attachments ?? [];
		arrAttach.forEach(iAttach => {
			intRowCount = renderRowItemAsLinkXL(
				intRowCount,
				iAttach.file?.fileName ?? iAttach.attachmentId,
				iAttach.file?.uri ?? '',
				'C',
				1,
				sheet,
				table,
				t,
			);
		});
	}

	table.commit();

	const strZipName = t('SideNavigation.Requirements', {
		ns: 'appShell/sidenavigation',
	});

	await createZIPXL(
		workbook,
		strZipName,
		t,
		arrTables,
		arrImagesUri,
		arrImagesName,
		[],
	);
};

export const renderRegulationNumberXL = (
	regulations?: Maybe<Regulation[]> | undefined,
) => {
	return (regulations ?? []).map(reg => reg.regulationNumber);
};

export const renderRegulationVersionXL = (
	regulatoryDocuments?: Maybe<RegulatoryDocument[]> | undefined,
) => {
	return (regulatoryDocuments ?? []).map(
		regdoc =>
			`${regdoc.regulation?.regulationNumber ?? ''}|${regdoc.name ?? ''}`,
	);
};
